/**
 * HOR-69: Presigned S3-URLs (SigV4) sind nur begrenzt gueltig – das Backend signiert fuer
 * 3600 s (verification.service presign()). Bleibt ein Verifizierungs-Detail laenger offen,
 * zeigt der Klick auf eine PDF anschliessend rohes S3-XML ("Request has expired") im iframe,
 * denn ein iframe feuert bei HTTP 403 kein onError (das Fehlerdokument wurde ja geladen).
 *
 * Ein HEAD-Request als Vorab-Pruefung taugt nicht: die HTTP-Methode ist Teil der Signatur,
 * HEAD auf eine fuer GET signierte URL liefert IMMER 403 (nicht von "abgelaufen" zu
 * unterscheiden), und die Bucket-CORS-Konfiguration fuer HEAD ist unbekannt.
 *
 * Der Ablauf laesst sich aber clientseitig aus der Query berechnen, die der AWS-SDK-Presigner
 * anhaengt: X-Amz-Date (Signaturzeitpunkt, YYYYMMDDTHHMMSSZ) + X-Amz-Expires (Sekunden).
 */

// Sicherheitspuffer gegen kleine Uhren-Abweichungen und die Ladezeit selbst.
export const PRESIGNED_URL_SAFETY_MARGIN_MS = 60 * 1000

/**
 * Frisch geladene Links sind per Definition gueltig (3600 s ab Signatur). Meldet die
 * Client-Uhr sie trotzdem als abgelaufen, geht die Uhr des Rechners falsch – dann darf
 * NICHT erneut geladen werden, sonst entstuende eine Endlosschleife (jede frische URL
 * saehe sofort wieder abgelaufen aus).
 */
export const MIN_URL_REFRESH_INTERVAL_MS = 5 * 60 * 1000

const AMZ_DATE_PATTERN = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/

// S3 behandelt die Parameternamen case-sensitiv, wir lesen tolerant.
const getQueryParam = (parsed, name) => {
  const wanted = name.toLowerCase()

  for (const [key, value] of parsed.searchParams) {
    if (key.toLowerCase() === wanted) return value
  }

  return null
}

/**
 * Ablaufzeitpunkt (Unix-ms) einer presigned URL oder null, wenn die URL keine
 * SigV4-Parameter traegt (z.B. Backend-Proxy-Pfad, Platzhalterbild) oder unparsebar ist.
 */
export const getPresignedExpiry = url => {
  if (typeof url !== 'string' || !url) return null

  let parsed

  try {
    // Presigned URLs sind immer absolut; relative Pfade (z.B. /missing.jpg) laufen nicht ab.
    parsed = new URL(url)
  } catch (e) {
    return null
  }

  const amzDate = getQueryParam(parsed, 'X-Amz-Date')
  const expires = getQueryParam(parsed, 'X-Amz-Expires')

  if (!amzDate || !expires) return null

  const match = AMZ_DATE_PATTERN.exec(amzDate)
  const seconds = Number(expires)

  if (!match || !Number.isFinite(seconds) || seconds <= 0) return null

  const signedAt = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]), Number(match[6]))

  if (!Number.isFinite(signedAt)) return null

  return signedAt + seconds * 1000
}

/**
 * true, wenn die URL abgelaufen ist oder innerhalb des Puffers ablaeuft.
 * URLs ohne Ablaufinformation gelten als gueltig (der Ladefehler-Fallback bleibt aktiv).
 */
export const isPresignedUrlStale = (url, marginMs = PRESIGNED_URL_SAFETY_MARGIN_MS) => {
  const expiresAt = getPresignedExpiry(url)

  if (expiresAt === null) return false

  return Date.now() + marginMs >= expiresAt
}

/**
 * Entscheidet, ob vor der Anzeige ein Neu-Laden der Daten (= frische Links) noetig ist.
 * `loadedAt` ist der Zeitpunkt, zu dem die Links vom Backend geholt wurden (siehe
 * MIN_URL_REFRESH_INTERVAL_MS fuer den Grund der Sperre).
 */
export const needsUrlRefresh = (urls, loadedAt) => {
  const list = Array.isArray(urls) ? urls.filter(Boolean) : []

  if (!list.some(url => isPresignedUrlStale(url))) return false

  return !loadedAt || Date.now() - loadedAt >= MIN_URL_REFRESH_INTERVAL_MS
}
